import { useEffect, useState } from "react";
import { kpsiGet } from "./kpsiApi";
import KpsiInterpreterGrid from "./KpsiInterpreterGrid";

type Detail = {
  test: {
    id: number;
    firstName: string;
    lastName: string;
    position: string;
    dateFaxDisplay: string;
    stageName: string;
    score: string;
    education?: string;
    adj?: string;
    range?: string;
    sort?: string;
    eng?: string;
    green?: string;
    red?: string;
    blue?: string;
    yellow?: string;
    notes?: string;
    downloadDisplay?: string;
    combinedDes: string;
    combinedWord: string;
  };
  work: Array<{ position?: string; startDate?: string; endDate?: string; reason?: string }>;
};

interface Props {
  id: number;
  onNormalView: (id: number) => void;
  onFlash: (msg: string) => void;
}

/** Dedicated print layout matching legacy KPSI Pages/Tests/Print.cshtml. */
export default function KpsiTestPrint({ id, onNormalView, onFlash }: Props) {
  const [data, setData] = useState<Detail | null>(null);

  useEffect(() => {
    document.body.classList.add("kpsi-print-mode");
    return () => document.body.classList.remove("kpsi-print-mode");
  }, []);

  useEffect(() => {
    void kpsiGet<Detail>(`/Kpsi/Tests/${id}`)
      .then(setData)
      .catch((e) => onFlash(e instanceof Error ? e.message : "Load failed"));
  }, [id]);

  if (!data) return <p className="kpsi-muted">Loading…</p>;
  const t = data.test;
  const fullName = `${t.firstName} ${t.lastName}`.trim();

  return (
    <div className="kpsi-print-doc">
      <p className="kpsi-no-print">
        <button type="button" onClick={() => onNormalView(id)}>Normal View</button>
        {" | "}
        <button type="button" onClick={() => window.print()}>Print</button>
      </p>

      <h1>
        Viewing Test for <em>{fullName}</em>
        <br />
        <small>
          Faxed on <em>{t.dateFaxDisplay}</em>
        </small>
      </h1>

      <p>
        <i>Current Stage:</i> <b>{t.stageName}</b>
        {" · "}
        <i>Download Date:</i> <b>{t.downloadDisplay || "—"}</b>
      </p>

      <KpsiInterpreterGrid test={t} />

      <h2>Education / Skills</h2>
      <pre className="kpsi-print-block">{t.education}</pre>

      <h2>Previous Work Experience</h2>
      {data.work?.length ? (
        <ol>
          {data.work.map((w, i) => (
            <li key={i}>
              {w.position} · {w.startDate} – {w.endDate} · {w.reason}
            </li>
          ))}
        </ol>
      ) : null}

      <h2>Notes</h2>
      <pre className="kpsi-print-block">{t.notes}</pre>
    </div>
  );
}
